import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDismiss } from './dismiss'

/**
 * A set of decisions, behind one control.
 *
 * Three buttons in a decision cell cost the column a third of the table's width
 * and gave it to labels the reader has already read nineteen times. The menu
 * gives that width back to *What was found* and *Comes from*, which is where the
 * reading actually happens. The bulk bar above the table uses the same control
 * for the same reason: *Approve selected* and *Decline selected* side by side put
 * the destructive one a slip away from the other.
 *
 * Two things the menu deliberately keeps that a naive one loses:
 *
 *   * **A refusal still says why.** §5.6 disables approving a scope rule until
 *     the blast radius is on screen. A greyed item behind a menu with no reason
 *     is worse than the button was, so `why` is rendered under the item — the
 *     reader learns the control exists *and* what satisfies it.
 *   * **The trigger says whose decisions these are.** Twenty rows each offering
 *     "Decision" tells a screen reader nothing about which row it is on.
 *
 * **The list is drawn at the page, beside its trigger, not inside the row.** A
 * table sits in a scrolling frame so it can be wider than the screen, and the frame
 * clipped a list hanging below the last row: the menu opened and could not be seen.
 * Placed from the trigger's own position, it opens below it, or above it where the
 * window has no room below, and closes if the page scrolls out from under it.
 */

export interface MenuItem {
  label: string
  onSelect: () => void
  disabled?: boolean
  /** Shown under the item when it is disabled. The reason, not the greying. */
  why?: string
  danger?: boolean
}

const GAP = 4
const MARGIN = 8

export function RowMenu({
  label,
  items,
  disabled,
  testId,
  text,
}: {
  label: string
  items: MenuItem[]
  disabled?: boolean
  testId?: string
  /** Words beside the dots, where the menu stands for one action with choices, such as Export. */
  text?: string
}) {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null)
  const close = useCallback(() => setOpen(false), [])
  const menu = useRef<HTMLDivElement>(null)
  const box = useDismiss<HTMLDivElement>(open, close, menu)

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    const trigger = box.current?.getBoundingClientRect()
    const list = menu.current?.getBoundingClientRect()
    if (!trigger || !list) return
    const below = trigger.bottom + GAP
    const above = trigger.top - GAP - list.height
    const top = below + list.height > window.innerHeight - MARGIN && above > MARGIN ? above : below
    const left = Math.max(MARGIN, Math.min(trigger.right - list.width, window.innerWidth - MARGIN - list.width))
    setPlace({ top, left })
  }, [open, box])

  // A list left where the row used to be is worse than none.
  useEffect(() => {
    if (!open) return
    const away = (event: Event) => {
      if (!menu.current?.contains(event.target as Node)) close()
    }
    window.addEventListener('scroll', away, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', away, true)
      window.removeEventListener('resize', close)
    }
  }, [open, close])

  return (
    <div className="row-menu" ref={box}>
      <button
        type="button"
        className={text ? 'dots labelled' : 'dots'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
      >
        {text && <span className="dots-text">{text}</span>}
        <span aria-hidden="true">&#8942;</span>
      </button>
      {open &&
        createPortal(
          <div
            ref={menu}
            className="row-menu-list"
            role="menu"
            data-testid={testId}
            style={place ? { top: place.top, left: place.left } : { top: 0, left: 0, visibility: 'hidden' }}
          >
            {items.map((item) => (
              <Fragment key={item.label}>
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  className={item.danger ? 'danger' : undefined}
                  onClick={() => {
                    setOpen(false)
                    item.onSelect()
                  }}
                >
                  {item.label}
                </button>
                {item.disabled && item.why && <p className="why">{item.why}</p>}
              </Fragment>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}
